import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import GraphQLFieldSelectorWrapper from 'analysis-tool/data-frame-creation/schemas/GraphQLFieldSelectorWrapper';
import QueryApiAllSchemaSelector, {
  QueryApiSchemaSelectorSchema,
} from 'analysis-tool/data-frame-creation/schemas/QueryApiAllSchemaSelector';
// eslint-disable-next-line import-access/jsdoc
import {FormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
// eslint-disable-next-line import-access/jsdoc
import {PLATE_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT} from 'analysis-tool/data-frame-creation/schemas/SchemaDatasetCreationConstants';
import {getFieldDisplayNameFn} from 'analysis-tool/data-frame-creation/schemas/SchemaDatasetCreationUtils';
import {pluralize} from 'BenchlingUtil';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import {QueryApiIntrospection} from 'query-api/useQueryApiIntrospection';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';

import {VariableHeaderOrNull} from '../StepConfigFieldsTemplate';
import {buildInitialQueryFormStateFromQueryString} from './LookUpQueryUtils';

interface LookUpSchemaSelectorStepperProps {
  setQueryFormState: (state: FormState) => void;
  queryApiIntrospection: QueryApiIntrospection;
  queryFormState: FormState;
  disabled?: boolean;
}

export default function LookUpSchemaSelectorStepper({
  setQueryFormState,
  queryApiIntrospection,
  queryFormState,
  disabled = false,
}: LookUpSchemaSelectorStepperProps): JSX.Element {
  const queryListingField =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryField(queryFormState.entityListingFieldName);

  const schemaId =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryFieldSchemaId(queryFormState.entityListingFieldName);

  const handleChangeSchema = useCallback(
    (schema: QueryApiSchemaSelectorSchema | null): void => {
      if (schema == null) {
        setQueryFormState({entityListingFieldName: null});
        return;
      }

      setQueryFormState(buildInitialQueryFormStateFromQueryString(schema.id, null, queryApiIntrospection));
    },
    [queryApiIntrospection, setQueryFormState]
  );

  return (
    <div>
      <QueryApiAllSchemaSelector
        schemaId={schemaId}
        handleSchemaChange={handleChangeSchema}
        queryApiIntrospection={queryApiIntrospection}
        schemaFilter={(schema) => APIUtil.isPlateSchema(schema.id) || APIUtil.isTagSchema(schema.id)}
        disabled={disabled}
      />
      {schemaId != null && queryFormState.entityListingFieldName != null && queryListingField != null && (
        <div className={css(styles.section)}>
          {(() => {
            const numSelected = countSelectedFields(queryFormState.entityFieldSelections.selections);
            return (
              <VariableHeaderOrNull
                variableName={`Select columns (${numSelected} ${pluralize(numSelected, 'column')} selected)`}
                isCompact={true}
              />
            );
          })()}
          <GraphQLFieldSelectorWrapper
            field={queryListingField}
            logicalDepthLimit={PLATE_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT}
            selectionSet={queryFormState.entityFieldSelections}
            setSelectionSet={(newSelectionSet) => {
              setQueryFormState({...queryFormState, entityFieldSelections: newSelectionSet});
            }}
            getFieldDisplayName={getFieldDisplayNameFn(queryListingField, queryApiIntrospection)}
            hideHeader={true}
            {...pick(
              queryApiIntrospection,
              'getTypeDisplayName',
              'graphQLSchema',
              'isDeprecatedField',
              'isBacklinkField',
              'isUserDefinedField',
              'isUserComputedField'
            )}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  section: {
    marginTop: 11,
  },
});
